/** HttpOnly cookie session helpers — do not store tokens in localStorage. */

export const SESSION_FETCH_OPTIONS = { credentials: "include" };

const SESSION_TIMEOUT_MS = 12_000;
const SESSION_EXCHANGE_TIMEOUT_MS = 45_000;

/** Remove legacy bearer tokens from older clients. */
export function clearLegacySessionToken() {
  try {
    localStorage.removeItem("session_token");
  } catch {
    // ignore
  }
}

async function fetchWithTimeout(url, options = {}, timeoutMs = SESSION_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

function isJsonSessionResponse(res) {
  if (!res.ok) return false;
  const contentType = res.headers.get("content-type") || "";
  return contentType.includes("application/json");
}

export async function exchangeBackendSession(
  accessToken,
  inviteToken,
  timeoutMs = SESSION_EXCHANGE_TIMEOUT_MS
) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch("/api/auth/session", {
      method: "POST",
      ...SESSION_FETCH_OPTIONS,
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        supabase_access_token: accessToken,
        invite_token: inviteToken || undefined,
      }),
    });

    const text = await res.text();
    let data = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { detail: text };
      }
    }
    return { ok: res.ok, status: res.status, data };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error("App session request timed out because the backend is taking too long. Please try again.");
    }
    throw new Error("Cannot reach the app backend. Please try signing in again.");
  } finally {
    window.clearTimeout(timer);
  }
}

export async function verifySession() {
  return (await probeSession()) === "authenticated";
}

export async function probeSession() {
  try {
    const res = await fetchWithTimeout("/api/auth/me", SESSION_FETCH_OPTIONS);
    if (res.status === 401) return "unauthenticated";
    if (isJsonSessionResponse(res)) return "authenticated";
    return "unavailable";
  } catch {
    return "unavailable";
  }
}
